import isEqual from 'lodash/isEqual'

import FluxComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import type { ComposerStore } from '../composerStore'
import { checkOmniParityAfterMirror } from './omniDrift'
import { isMirrorWriteInProgress, runMirrorWrite } from './mirrorGate'

/**
 * Subscribe to Zustand omni state changes and write plain fields back to the
 * Flux omni draft.
 *
 * The omni draft's `editorState` intentionally remains Flux-owned — legacy
 * editor consumers read it directly — so this mirror leaves it untouched.
 * `assets` has no Zustand writer yet (no reverse projection exists from a flat
 * `OmniAsset[]` back into Flux's order-sensitive `images`/`videos`/`gif`/
 * `document` fields) — see `setOmniAssets` in `postSlice`.
 *
 * INVARIANT: every mirrored field here must have its Zustand writers wired in
 * the same PR that adds the field. A Zustand writer without a mirror line makes
 * flag-on writes invisible to the legacy save path; a mirror line without a
 * writer is dead code.
 *
 * @param flushPendingFluxMirror Applies any pending (debounced) Flux→Zustand
 * asset pass. Called before the parity check so a mid-debounce asset change is
 * not misread as drift.
 * @returns An unsubscribe function; further Zustand writes will not reach Flux
 * after it is called.
 */
export function installZustandToFluxMirror(
  store: ComposerStore,
  { flushPendingFluxMirror }: { flushPendingFluxMirror?: () => void } = {},
): () => void {
  // Stamped once per mirror install — see `checkOmniParityAfterMirror`.
  const installedAt = performance.now()

  const unsubscribe = store.subscribe((state, prev) => {
    if (state.omni === prev.omni) return
    if (isMirrorWriteInProgress()) return

    const fluxOmni = FluxComposerStore.getDraft('omni')
    if (!fluxOmni) return

    const textChanged = state.omni.text !== fluxOmni.text
    const urlsChanged = !isEqual(state.omni.urls, fluxOmni.urls)
    const unshortenedChanged = !isEqual(
      state.omni.unshortenedUrls,
      fluxOmni.unshortenedUrls,
    )
    const aiAssistedChanged = state.omni.aiAssisted !== fluxOmni.aiAssisted

    if (
      !textChanged &&
      !urlsChanged &&
      !unshortenedChanged &&
      !aiAssistedChanged
    )
      return

    runMirrorWrite(() => {
      if (textChanged) {
        fluxOmni.text = state.omni.text
      }
      if (urlsChanged) {
        fluxOmni.urls = [...state.omni.urls]
      }
      if (unshortenedChanged) {
        fluxOmni.unshortenedUrls = [...state.omni.unshortenedUrls]
      }
      if (aiAssistedChanged) {
        fluxOmni.aiAssisted = state.omni.aiAssisted
      }
      FluxComposerStore.emitChange()
    })

    flushPendingFluxMirror?.()
    checkOmniParityAfterMirror(
      store,
      'zustandToFlux',
      performance.now() - installedAt,
    )
  })

  return unsubscribe
}
